import p5Types from 'p5';
import gsap from 'gsap';

import { Card as CardType } from '@src/lib/types';
import {
  getCardImageString,
  p5GetCardImageSize,
  p5PreloadCardImages,
} from '@src/lib/utils/card';
import p5Card from './p5Card';

import {
  registerMouseEventHandlers,
  teardownMouseEventHandlers,
} from './p5MouseEvents';

let images: Record<string, any> | null = null;

const CARD_HEIGHT_PERCENTAGE_OF_CANVAS = 0.8;

const defaultCardSizeMultiplier = 0.5;
let cardSizeMultiplier = defaultCardSizeMultiplier;
const getCardSize = () => p5GetCardImageSize(cardSizeMultiplier);

let currentCardSize = getCardSize();

const defaultCardSpacing = currentCardSize[0] * 0.6;
let cardSpacing = defaultCardSpacing;
let currentCards = [] as p5Card[];

let selectedCardIndex = -1;

let isDragging = false;
let handXMod = 0;
let lastHandXMod: null | number = null;
const touchStart = { x: 0, y: 0 };
const touchDelta = { x: 0, y: 0 };
let scrollDirection: 'left' | 'right' | null = null;

let handleCardClicked: ((card: CardType) => void) | null = null;

// handles mouse down events
const onTouchStarted = (e: any) => {
  if (e.target.id !== 'defaultCanvas0') return;
  touchStart.x = e.touches?.[0].pageX;
  touchStart.y = e.touches?.[0].pageY;
  touchDelta.x = 0;
  touchDelta.y = 0;
  isDragging = false;
};

// handles drag events
const onTouchMoved = (e: any) => {
  if (e.target.id !== 'defaultCanvas0') return;
  if (Math.abs(e.movementX) <= 2) return false;
  if (e.movementX) {
    lastHandXMod = null;

    touchDelta.x = e.movementX;
    handXMod = handXMod + touchDelta.x;
  } else if (e.touches?.[0]?.pageX) {
    touchDelta.x = e.touches?.[0].pageX - touchStart.x;
    handXMod =
      lastHandXMod !== null ? lastHandXMod + touchDelta.x : touchDelta.x;
  }

  scrollDirection = touchDelta.x > 0 ? 'right' : 'left';
  isDragging = true;
  return false;
};

const onScroll = (e: any) => {
  if (e.target.id !== 'defaultCanvas0') return;
  handXMod = handXMod + e.wheelDelta;
  return false;
};

// handles drag end and click events
const onTouchEnded = (e: any) => {
  if (e.target.id !== 'defaultCanvas0') return;
  if (isDragging) {
    isDragging = false;
    lastHandXMod = handXMod;
    scrollDirection = null;
  } else {
    lastHandXMod = handXMod;
    // handle mouse click
    if (selectedCardIndex !== -1 && handleCardClicked) {
      handleCardClicked(currentCards[selectedCardIndex].card);
    }
  }
  return false;
};

export const onResized = (p5: p5Types, width: number, height: number) => {
  p5.resizeCanvas(width, height);
  handXMod = 0;
  lastHandXMod = null;
};

// called during p5 setup
export const setup = (
  p5: p5Types,
  _handleCardClicked: (card: CardType) => void,
) => {
  images = p5PreloadCardImages(p5);
  handleCardClicked = _handleCardClicked;
  registerMouseEventHandlers(
    p5,
    onTouchStarted,
    onTouchMoved,
    onTouchEnded,
    onScroll,
  );
};

export const teardown = (p5: p5Types) => {
  teardownMouseEventHandlers(p5);
};

const getHandWidth = (numCards: number) => {
  return numCards * cardSpacing - cardSpacing + getCardSize()[0];
};

export const p5GetCards = (p5: p5Types, cards: CardType[]): p5Card[] => {
  const p5Cards: p5Card[] = [];

  cards.forEach((card) => {
    const cardImage = getImage(getCardImageString(card));
    const cardSize = getCardSize();
    p5Cards.push(
      new p5Card(
        card,
        cardSize[0],
        cardSize[1],
        cardSizeMultiplier,
        cardImage,
        p5,
      ),
    );
  });

  // currentCards = p5Cards;
  updateCurrentCards(p5.height, p5.width);

  return p5Cards;
};

function mouseOverRect(
  p5: p5Types,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  if (
    p5.mouseX > x &&
    p5.mouseX < x + w &&
    p5.mouseY > y &&
    p5.mouseY < y + h
  ) {
    return true;
  } else {
    return false;
  }
}

const getImage = (imageString: string): any => {
  if (images?.[imageString as keyof typeof images]) {
    return images[imageString as keyof typeof images];
  }
  return images?.['card_back.jpg'];
};

// calculates and sets the card size multiplier
export const updateCurrentCards = (
  containerWidth: number,
  containerHeight: number,
) => {
  cardSizeMultiplier = defaultCardSizeMultiplier;
  currentCardSize = getCardSize();
  const currentHeight = currentCardSize[1];
  const newHeight = containerHeight * CARD_HEIGHT_PERCENTAGE_OF_CANVAS;
  cardSizeMultiplier = (newHeight / currentHeight) * defaultCardSizeMultiplier;
  cardSpacing = cardSizeMultiplier * currentCardSize[0];
  currentCardSize = getCardSize();

  // update currentCards
  currentCards?.forEach((card, index) => {
    p5Card.cardSizeMultiplier = cardSizeMultiplier;
    card.width = currentCardSize[0];
    card.height = currentCardSize[1];

    const handWidth = getHandWidth(currentCards.length);
    const handX = (containerWidth - handWidth) / 2;
    let x_offset = index * cardSpacing + handX + handXMod;

    if (
      currentCards[0].x_offset >= containerWidth * 0.5 &&
      scrollDirection === 'right'
    ) {
      x_offset = card.x_offset;
    }

    const y_offset =
      (containerHeight * (1 - CARD_HEIGHT_PERCENTAGE_OF_CANVAS)) / 2;

    card.x_offset = x_offset;
    card.y_offset = y_offset;
  });
};

const selectCardTween = (card: p5Card) => {
  const tl = gsap.timeline();
  const height = card.height * 1.03;
  const width = card.width * 1.03;
  const y_offset_mod = (-1 * (height - card.height)) / 2;
  const x_offset_mod = (-1 * (width - card.width)) / 2;
  tl.to(card, { duration: 0.1, width, height, y_offset_mod, x_offset_mod });
};

const deselectCardTween = (card: p5Card) => {
  const tl = gsap.timeline();
  tl.to(card, {
    duration: 0.1,
    width: currentCardSize[0],
    height: currentCardSize[1],
    y_offset_mod: 0,
    x_offset_mod: 0,
  });
};

const selectCard = (card: p5Card, index: number) => {
  if (selectedCardIndex === index) return;
  if (selectedCardIndex !== -1) deselectCard(currentCards[selectedCardIndex]);
  selectedCardIndex = index;
  selectCardTween(card);
};

const deselectCard = (card: p5Card) => {
  selectedCardIndex = -1;
  deselectCardTween(card);
};

export const draw = (p5: p5Types, cards: p5Card[]) => {
  if (!images) return;

  currentCards = cards;

  // BEGIN update cards //
  updateCurrentCards(p5.width, p5.height);
  const cardIsTooSmall =
    currentCardSize[1] < p5.height * CARD_HEIGHT_PERCENTAGE_OF_CANVAS;
  const cardIsTooBig =
    currentCardSize[1] > p5.height * CARD_HEIGHT_PERCENTAGE_OF_CANVAS;

  // find card size multiplier
  if (cardIsTooSmall || cardIsTooBig) {
    updateCurrentCards(p5.width, p5.height);
  }

  // END update cards sizing //

  // BEGIN handle scrolling constraints //
  const handWidth = cards.length ? getHandWidth(cards.length) : null;

  // when handWidth is less than container, lock scrolling and center hand
  if (handWidth && handWidth < p5.width) {
    handXMod = 0;
    lastHandXMod = null;
    updateCurrentCards(p5.width, p5.height);
  }
  // when hand has been scrolled all the way to the left, lock scrolling
  if (handWidth && handXMod <= -(handWidth / 2)) {
    handXMod = -(handWidth / 2);
    updateCurrentCards(p5.width, p5.height);
  }

  // when hand has been scrolled all the way to the right, lock scrolling
  if (handWidth && handXMod >= handWidth / 2) {
    handXMod = handWidth / 2;
    updateCurrentCards(p5.width, p5.height);
  }

  // BEGIN handle scrolling constraints //

  // BEGIN handle card selection //
  const hoveredCardIndexes = [] as number[];

  if (!isDragging) {
    cards.forEach((card, index) => {
      if (
        mouseOverRect(p5, card.x_offset, card.y_offset, card.width, card.height)
      ) {
        hoveredCardIndexes.push(index);
      }
    });
  }

  if (hoveredCardIndexes.length > 1) {
    const index = hoveredCardIndexes[hoveredCardIndexes.length - 1];
    selectCard(currentCards[index], index);
  } else if (hoveredCardIndexes.length === 1) {
    const index = hoveredCardIndexes[0];
    selectCard(currentCards[index], index);
  } else if (hoveredCardIndexes.length === 0 && selectedCardIndex !== -1) {
    deselectCard(currentCards[selectedCardIndex]);
    selectedCardIndex = -1;
  }

  if (selectedCardIndex === -1) p5.cursor('default');

  cards.forEach((card, index) => {
    const highlight = selectedCardIndex === index;

    if (highlight) {
      p5.cursor('pointer');
    }

    card.draw(p5, highlight);
  });

  // END handle card selection //
};
